import { TransactionTrendsResponse } from '@/store/transactionsApi';
import { CashFlowTabMode } from './CashFlowDetailsBarChart';

const TOP_CATEGORY_LIMIT = 5;
const OTHER_KEY = '__other__';

export interface TrendBarSeries {
  dataKey: string;
  label: string;
  stack: string;
}

export type TrendBarRow = { date: Date } & Record<string, number | Date>;

export interface TrendBarChartData {
  dataset: TrendBarRow[];
  series: TrendBarSeries[];
}

export interface TrendBarLabels {
  income: string;
  spending: string;
  other: (count: number) => string;
}

type TrendSeries = TransactionTrendsResponse['Records']['series'][number];

const monthToDate = (name: string): Date => {
  const [month, year] = name.split('/');
  return new Date(Number(year), Number(month) - 1, 1);
};

type BarValueAt = (series: TrendSeries | undefined, index: number) => number;

const signedValueAt: BarValueAt = (series, index) => series?.data[index] ?? 0;

const spendingValueAt: BarValueAt = (series, index) =>
  Math.abs(signedValueAt(series, index));

const isSpendingSeries = (item: TrendSeries) =>
  item.name.trim().toLowerCase() === 'spending';

const buildBothSeries = (
  series: TrendSeries[],
  monthlyNets: number[],
  months: Date[],
  labels: TrendBarLabels,
): TrendBarChartData => {
  const spending = series.find(isSpendingSeries);

  return {
    dataset: months.map((date, index) => {
      const spendingValue = spendingValueAt(spending, index);
      return {
        date,
        income: monthlyNets[index] + spendingValue,
        spending: spendingValue,
      };
    }),
    series: [
      { dataKey: 'income', label: labels.income, stack: 'income' },
      { dataKey: 'spending', label: labels.spending, stack: 'spending' },
    ],
  };
};

const buildCategorySeries = (
  series: TrendSeries[],
  months: Date[],
  labels: TrendBarLabels,
  valueAt: BarValueAt,
): TrendBarChartData => {
  const ranked = series
    .map((item) => ({
      item,
      total: months.reduce(
        (sum, _date, index) => sum + Math.abs(valueAt(item, index)),
        0,
      ),
    }))
    .filter(({ total }) => total > 0)
    .sort((a, b) => b.total - a.total)
    .map(({ item }) => item);

  const top = ranked.slice(0, TOP_CATEGORY_LIMIT);
  const rest = ranked.slice(TOP_CATEGORY_LIMIT);
  const topKeys = top.map((_item, index) => `category${String(index)}`);

  const dataset = months.map((date, monthIndex) => {
    const row: TrendBarRow = { date };
    top.forEach((item, index) => {
      row[topKeys[index]] = valueAt(item, monthIndex);
    });
    if (rest.length > 0) {
      row[OTHER_KEY] = rest.reduce(
        (sum, item) => sum + valueAt(item, monthIndex),
        0,
      );
    }
    return row;
  });

  const chartSeries: TrendBarSeries[] = top.map((item, index) => ({
    dataKey: topKeys[index],
    label: item.name,
    stack: 'categories',
  }));
  if (rest.length > 0) {
    chartSeries.push({
      dataKey: OTHER_KEY,
      label: labels.other(rest.length),
      stack: 'categories',
    });
  }

  return { dataset, series: chartSeries };
};

export const buildTrendBarChartData = (
  trends: TransactionTrendsResponse | undefined,
  mode: CashFlowTabMode,
  labels: TrendBarLabels,
): TrendBarChartData => {
  const categories = trends?.Records.categories ?? [];
  const series = trends?.Records.series ?? [];
  const months = categories.map((category) => monthToDate(category.name));

  const chartData =
    mode === 'both'
      ? buildBothSeries(
          series,
          categories.map((category) => category.value),
          months,
          labels,
        )
      : buildCategorySeries(
          series,
          months,
          labels,
          mode === 'spending' ? spendingValueAt : signedValueAt,
        );

  const hasValues = chartData.dataset.some((row) =>
    chartData.series.some((item) => Number(row[item.dataKey]) !== 0),
  );

  if (!hasValues) return { dataset: [], series: [] };

  return {
    ...chartData,
    dataset: [...chartData.dataset].sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    ),
  };
};
